import { Recipe, Protein } from '../types';
import { ExclusionRulesConfig } from '../types/config';
import { ProteinAliasMap, buildAliasLookup, buildProteinAliasLookup, canonicalizeToken } from './proteinAliases';

interface RecipeFilterOptions {
  proteinAliasLookup?: ProteinAliasMap;
  pathologies?: string[];
  subPathologies?: Record<string, string[]>;
  exclusionRules?: Partial<ExclusionRulesConfig>;
}

const NONE_LIKE_VALUES = new Set(['', 'ninguna', 'ninguno', 'none', 'null', 'na', 'n_a']);

const canonicalizeValues = (values: string[] | undefined, lookup: Record<string, string>): Set<string> =>
  new Set(
    (values || [])
      .map((value) => canonicalizeToken(String(value), lookup))
      .filter((value) => value && !NONE_LIKE_VALUES.has(value)),
  );

const recipeProductIds = (recipe: Recipe): string[] => {
  const ids = new Set<string>();
  if (recipe.productId) ids.add(String(recipe.productId));
  Object.values(recipe.variants || {}).forEach((variant) => {
    if (variant?.productId) ids.add(String(variant.productId));
  });
  return Array.from(ids);
};

const recipeMatchesProductRule = (recipe: Recipe, blockedIds: Set<string>, allowedIds?: Set<string>): boolean => {
  const productIds = recipeProductIds(recipe);
  // The grouped recipe's canonical productId is the 400 g product (the base
  // chosen in AppConfigContext). Step 10 must match the product that the first
  // calculation will actually send. An 800-only block therefore keeps the
  // 400 g recipe card available; a 400 g block removes the card. The admin
  // editor normally adds both variants together for recipe-wide exclusions.
  const primaryProductId = recipe.productId ? String(recipe.productId) : productIds[0];
  if (!primaryProductId) return false;
  if (allowedIds && allowedIds.size > 0 && !allowedIds.has(primaryProductId)) return true;
  return blockedIds.has(primaryProductId);
};

const getRecipeAllergens = (recipe: Recipe, proteinLookup: Record<string, string>): Set<string> => {
  const allergens = new Set<string>();
  const proteins = recipe.proteinTypes?.length ? recipe.proteinTypes : [recipe.proteinType];
  proteins.forEach((protein) => {
    const canonical = canonicalizeToken(String(protein), proteinLookup);
    if (canonical) allergens.add(canonical);
  });
  (recipe.allergens || []).forEach((allergen) => {
    const canonical = canonicalizeToken(String(allergen), proteinLookup);
    if (canonical) allergens.add(canonical);
  });
  return allergens;
};

const intersects = (left: Set<string>, right: Set<string>): boolean =>
  Array.from(left).some((value) => right.has(value));

// Filtrar recetas según alergias y reglas clínicas. La fuente de recetas
// (`recipesSource`) SIEMPRE la aporta el llamador desde el catálogo del backend.
// El backend repite la validación al calcular: esta función mantiene Step 10 en
// paridad para no ofrecer una receta que se descartará después.
export const getAvailableRecipes = (
  allergies: Array<Protein | string>,
  recipesSource: Recipe[],
  options?: RecipeFilterOptions,
): Recipe[] => {
  const exclusionRules = options?.exclusionRules || {};
  const proteinLookup = buildProteinAliasLookup(options?.proteinAliasLookup || exclusionRules.protein_aliases);
  const pathologyLookup = buildAliasLookup(exclusionRules.pathology_aliases);
  const canonicalAllergies = canonicalizeValues((allergies || []).map(String), proteinLookup);
  const dogPathologies = canonicalizeValues(options?.pathologies, pathologyLookup);
  const normalizedSubPathologies = new Map<string, Set<string>>();
  Object.entries(options?.subPathologies || {}).forEach(([pathology, subPathologies]) => {
    const canonicalPathology = canonicalizeToken(pathology, pathologyLookup);
    const current = normalizedSubPathologies.get(canonicalPathology) || new Set<string>();
    canonicalizeValues(subPathologies, {}).forEach((subPathology) => current.add(subPathology));
    normalizedSubPathologies.set(canonicalPathology, current);
  });
  const globallyBlockedPathologies = canonicalizeValues(exclusionRules.blocked_pathologies, pathologyLookup);
  const pathologyRules = Object.fromEntries(
    Object.entries(exclusionRules.pathology_rules || {}).map(([pathology, rule]) => [
      canonicalizeToken(pathology, pathologyLookup),
      rule,
    ]),
  );
  const allergenRuleProductIds = new Map<string, Set<string>>();
  Object.entries(exclusionRules.allergen_rules || {}).forEach(([allergen, rule]) => {
    const canonical = canonicalizeToken(allergen, proteinLookup);
    const current = allergenRuleProductIds.get(canonical) || new Set<string>();
    (rule.blocked_product_ids || []).forEach((id) => current.add(String(id)));
    allergenRuleProductIds.set(canonical, current);
  });
  const legacyBlockedProteins = exclusionRules.pathology_blocked_protein_types || {};
  const legacyBlockedProductIds = exclusionRules.pathology_blocked_product_ids || {};

  return recipesSource.map((recipe) => {
    // The product protein(s) and explicit allergens are both relevant. A recipe
    // with secondary allergens must not lose its main protein from the check.
    const allergensForRecipe = getRecipeAllergens(recipe, proteinLookup);
    let isExcluded = false;
    const excludedBy: string[] = [];
    allergensForRecipe.forEach((allergen) => {
      if (canonicalAllergies.has(allergen)) {
        isExcluded = true;
        if (!excludedBy.includes(allergen)) excludedBy.push(allergen);
      }
    });

    let exclusionReason: Recipe['exclusionReason'] = isExcluded ? 'allergy' : undefined;

    for (const allergy of Array.from(canonicalAllergies)) {
      const blockedProductIds = allergenRuleProductIds.get(allergy) || new Set<string>();
      if (recipeMatchesProductRule(recipe, blockedProductIds)) {
        isExcluded = true;
        exclusionReason = 'allergy';
        if (!excludedBy.includes(allergy)) excludedBy.push(allergy);
      }
    }

    for (const pathology of Array.from(dogPathologies)) {
      const legacyProteins = canonicalizeValues(
        Object.entries(legacyBlockedProteins)
          .filter(([key]) => canonicalizeToken(key, pathologyLookup) === pathology)
          .flatMap(([, values]) => values || []),
        proteinLookup,
      );
      const legacyProductIds = new Set(
        Object.entries(legacyBlockedProductIds)
          .filter(([key]) => canonicalizeToken(key, pathologyLookup) === pathology)
          .flatMap(([, values]) => values || [])
          .map(String),
      );
      let excludedForPathology =
        globallyBlockedPathologies.has(pathology) ||
        intersects(allergensForRecipe, legacyProteins) ||
        recipeMatchesProductRule(recipe, legacyProductIds);

      const rule = pathologyRules[pathology];
      if (rule?.mode === 'deny_all') excludedForPathology = true;
      if (rule?.mode === 'filter_products') {
        const blockedProteins = canonicalizeValues(rule.blocked_protein_types, proteinLookup);
        const blockedProductIds = new Set((rule.blocked_product_ids || []).map(String));
        const allowedProductIds = new Set((rule.allowed_product_ids || []).map(String));
        excludedForPathology =
          excludedForPathology ||
          intersects(allergensForRecipe, blockedProteins) ||
          recipeMatchesProductRule(recipe, blockedProductIds, allowedProductIds);
      }

      const selectedSubPathologies = normalizedSubPathologies.get(pathology) || new Set<string>();
      for (const subPathology of Array.from(selectedSubPathologies)) {
        const subRule = Object.entries(rule?.sub_pathology_rules || {}).find(
          ([key]) => canonicalizeToken(key, {}) === subPathology,
        )?.[1];
        if (subRule?.mode === 'deny_all') {
          excludedForPathology = true;
          break;
        }
        if (subRule?.mode === 'filter_products') {
          const subBlockedProteins = canonicalizeValues(subRule.blocked_protein_types, proteinLookup);
          const subBlockedProductIds = new Set((subRule.blocked_product_ids || []).map(String));
          const subAllowedProductIds = new Set((subRule.allowed_product_ids || []).map(String));
          if (
            intersects(allergensForRecipe, subBlockedProteins) ||
            recipeMatchesProductRule(recipe, subBlockedProductIds, subAllowedProductIds)
          ) {
            excludedForPathology = true;
            break;
          }
        }
      }

      if (excludedForPathology) {
        isExcluded = true;
        // Preserve the more specific allergy message when both rules match.
        if (exclusionReason !== 'allergy') exclusionReason = 'pathology';
        if (!excludedBy.includes(pathology)) excludedBy.push(pathology);
      }
    }

    return { ...recipe, isExcluded, excludedBy, exclusionReason };
  });
};
